import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationPermission } from '../../hooks/useNotificationPermission';
import { usePWA } from '../../hooks/usePWA';
import toast from 'react-hot-toast';
import {
  Bell,
  BellOff,
  Smartphone,
  Monitor,
  Chrome,
  Settings,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  Play,
  Pause,
  RefreshCw
} from 'lucide-react';

export default function NotificationSetup() {
  const navigate = useNavigate();
  const { 
    permission, 
    isSupported, 
    isPushSupported, 
    hasActiveSubscription,
    needsAttention,
    requestPermission,
    refreshStatus 
  } = useNotificationPermission();
  
  const { 
    registerPushSubscription, 
    sendNotification,
    vapidPublicKey 
  } = usePWA();

  const [isEnabling, setIsEnabling] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  const handleEnableNotifications = async () => {
    setIsEnabling(true);
    try {
      // Primeiro solicita permissão
      const permissionResult = await requestPermission();
      
      if (permissionResult === 'granted') {
        // Depois registra para push notifications
        await registerPushSubscription();
        
        // Envia notificação de teste
        await sendNotification('Notificações Ativadas!', {
          body: 'Você receberá alertas importantes do sistema.',
          icon: '/icons/android/android-launchericon-192-192.png',
          tag: 'setup-success'
        });
        
        toast.success('Notificações ativadas com sucesso!');
        
        // Atualiza o status
        setTimeout(() => {
          refreshStatus();
        }, 1000);
        
      } else if (permissionResult === 'denied') {
        toast.error('Permissão negada. Siga as instruções abaixo para ativar manualmente.');
      }
      
    } catch (error) {
      console.error('Erro ao ativar notificações:', error);
      toast.error('Erro ao ativar notificações: ' + (error as Error).message);
    } finally {
      setIsEnabling(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      await sendNotification('Teste de Notificação', {
        body: 'Esta é uma notificação de teste do sistema.',
        icon: '/icons/android/android-launchericon-192-192.png',
        badge: '/icons/android/android-launchericon-96-96.png',
        tag: 'test-notification'
      });
      toast.success('Notificação de teste enviada!');
    } catch (error) {
      console.error('Erro ao enviar notificação de teste:', error);
      toast.error('Erro ao enviar notificação de teste');
    }
  };

  const getBrowserIcon = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('chrome')) return <Chrome className="h-6 w-6" />;
    if (userAgent.includes('firefox')) return <Monitor className="h-6 w-6" />; // Using Monitor as fallback
    if (userAgent.includes('safari')) return <Monitor className="h-6 w-6" />; // Using Monitor as fallback
    return <Monitor className="h-6 w-6" />;
  };

  const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) return 'android';
    if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
    return 'desktop';
  };

  const deviceType = getDeviceType();

  const getStatusColor = () => {
    if (!isSupported) return 'text-red-600 bg-red-50 border-red-200';
    if (permission === 'granted' && hasActiveSubscription) return 'text-green-600 bg-green-50 border-green-200';
    if (permission === 'denied') return 'text-red-600 bg-red-50 border-red-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const getStatusText = () => {
    if (!isSupported) return 'Não Suportado';
    if (permission === 'granted' && hasActiveSubscription) return 'Ativadas e Funcionando';
    if (permission === 'granted' && !hasActiveSubscription) return 'Permissão Concedida - Configurando...';
    if (permission === 'denied') return 'Permissão Negada';
    return 'Aguardando Configuração';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Voltar
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Bell className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurar Notificações</h1>
              <p className="text-gray-600">Ative as notificações para receber alertas importantes do sistema</p>
            </div>
          </div>
        </div>

        {/* Status Atual */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-blue-600" />
            Status Atual
          </h2>
          
          <div className={`p-4 rounded-xl border ${getStatusColor()}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {permission === 'granted' && hasActiveSubscription ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-yellow-600" />
                )}
                <div>
                  <p className="font-medium">{getStatusText()}</p>
                  <p className="text-sm opacity-75">
                    Navegador: {isSupported ? 'Suportado' : 'Não Suportado'} | 
                    Push: {isPushSupported ? 'Suportado' : 'Não Suportado'}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={refreshStatus}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-lg transition-colors"
                  title="Atualizar status"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Ações Rápidas */}
          <div className="mt-6 flex flex-wrap gap-3">
            {isSupported && permission !== 'granted' && (
              <button
                onClick={handleEnableNotifications}
                disabled={isEnabling}
                className="flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Bell className="h-4 w-4 mr-2" />
                {isEnabling ? 'Ativando...' : 'Ativar Notificações'}
              </button>
            )}
            
            {permission === 'granted' && (
              <button
                onClick={sendTestNotification}
                className="flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              >
                <Play className="h-4 w-4 mr-2" />
                Testar Notificação
              </button>
            )}
          </div>
        </div>

        {/* Instruções por Navegador */}
        <div className="grid gap-6 mb-8">
          {/* Chrome */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Chrome className="h-6 w-6 mr-3 text-blue-600" />
              Google Chrome
            </h2>
            
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <h3 className="font-medium text-blue-900 mb-2">Método Automático</h3>
                <p className="text-blue-800 text-sm mb-3">
                  Clique no botão "Ativar Notificações" acima e permita quando solicitado.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Método Manual</h3>
                <ol className="text-gray-700 text-sm space-y-2">
                  <li>1. Clique no ícone de cadeado 🔒 na barra de endereços</li>
                  <li>2. Encontre "Notificações" na lista</li>
                  <li>3. Altere de "Bloquear" para "Permitir"</li>
                  <li>4. Recarregue a página</li>
                </ol>
              </div>

              {deviceType === 'android' && (
                <div className="bg-green-50 p-4 rounded-xl">
                  <h3 className="font-medium text-green-900 mb-2">Android - Configurações do Sistema</h3>
                  <ol className="text-green-800 text-sm space-y-2">
                    <li>1. Abra as Configurações do Android</li>
                    <li>2. Vá em "Apps" ou "Gerenciador de Aplicativos"</li>
                    <li>3. Encontre "Chrome" na lista</li>
                    <li>4. Toque em "Notificações"</li>
                    <li>5. Ative "Mostrar notificações"</li>
                  </ol>
                </div>
              )}
            </div>
          </div>

          {/* Firefox */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Monitor className="h-6 w-6 mr-3 text-orange-600" />
              Mozilla Firefox
            </h2>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-xl">
                <h3 className="font-medium text-orange-900 mb-2">Método Automático</h3>
                <p className="text-orange-800 text-sm mb-3">
                  Clique no botão "Ativar Notificações" acima e escolha "Permitir" na popup.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Método Manual</h3>
                <ol className="text-gray-700 text-sm space-y-2">
                  <li>1. Clique no menu ☰ (três linhas) no canto superior direito</li>
                  <li>2. Selecione "Configurações"</li>
                  <li>3. Vá para "Privacidade e Segurança"</li>
                  <li>4. Role até "Permissões" e clique em "Notificações" → "Configurações"</li>
                  <li>5. Encontre este site e altere para "Permitir"</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Safari */}
          {deviceType === 'ios' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Monitor className="h-6 w-6 mr-3 text-blue-600" />
                Safari (iPhone/iPad)
              </h2>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-xl">
                  <h3 className="font-medium text-blue-900 mb-2">Configurações do Safari</h3>
                  <ol className="text-blue-800 text-sm space-y-2">
                    <li>1. Clique no botão "Ativar Notificações" acima</li>
                    <li>2. Quando aparecer a popup, toque em "Permitir"</li>
                    <li>3. Se não aparecer, vá em Configurações → Safari → Notificações</li>
                  </ol>
                </div>

                <div className="bg-green-50 p-4 rounded-xl">
                  <h3 className="font-medium text-green-900 mb-2">Configurações do iOS</h3>
                  <ol className="text-green-800 text-sm space-y-2">
                    <li>1. Abra as Configurações do iOS</li>
                    <li>2. Vá em "Notificações"</li>
                    <li>3. Encontre "Safari" na lista</li>
                    <li>4. Ative "Permitir Notificações"</li>
                    <li>5. Configure som, banners e central de notificações</li>
                  </ol>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PWA Installation */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Smartphone className="h-6 w-6 mr-3 text-purple-600" />
            Instalar como Aplicativo (Recomendado)
          </h2>
          
          <div className="bg-purple-50 p-4 rounded-xl mb-4">
            <p className="text-purple-800 text-sm mb-3">
              Para a melhor experiência com notificações, instale o sistema como um aplicativo em seu dispositivo.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-gray-50 p-4 rounded-xl">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <Smartphone className="h-4 w-4 mr-2" />
                Mobile
              </h3>
              <ol className="text-gray-700 text-sm space-y-1">
                <li>1. Toque no menu do navegador (⋮ ou ⚙️)</li>
                <li>2. Procure "Adicionar à tela inicial" ou "Instalar app"</li>
                <li>3. Confirme a instalação</li>
                <li>4. Use o ícone na tela inicial</li>
              </ol>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl">
              <h3 className="font-medium text-gray-900 mb-2 flex items-center">
                <Monitor className="h-4 w-4 mr-2" />
                Desktop
              </h3>
              <ol className="text-gray-700 text-sm space-y-1">
                <li>1. Procure o ícone de instalação na barra de endereços</li>
                <li>2. Ou use o menu do navegador → "Instalar [Nome do App]"</li>
                <li>3. Confirme a instalação</li>
                <li>4. Use como aplicativo independente</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="h-6 w-6 mr-3 text-amber-600" />
            Resolução de Problemas
          </h2>
          
          <div className="space-y-4">
            <details className="bg-gray-50 p-4 rounded-xl">
              <summary className="font-medium text-gray-900 cursor-pointer">
                As notificações não aparecem
              </summary>
              <div className="mt-3 text-gray-700 text-sm space-y-2">
                <p>• Verifique se as notificações estão ativadas no navegador</p>
                <p>• Confirme se as notificações do sistema operacional estão habilitadas</p>
                <p>• Teste enviando uma notificação manual</p>
                <p>• Recarregue a página e tente novamente</p>
              </div>
            </details>

            <details className="bg-gray-50 p-4 rounded-xl">
              <summary className="font-medium text-gray-900 cursor-pointer">
                Permissão foi negada acidentalmente
              </summary>
              <div className="mt-3 text-gray-700 text-sm space-y-2">
                <p>• No Chrome: Clique no cadeado → Notificações → Permitir</p>
                <p>• No Firefox: Menu → Configurações → Privacidade → Notificações</p>
                <p>• No Safari: Configurações → Safari → Notificações</p>
                <p>• Limpe os dados do site se necessário</p>
              </div>
            </details>

            <details className="bg-gray-50 p-4 rounded-xl">
              <summary className="font-medium text-gray-900 cursor-pointer">
                Notificações funcionam mas são silenciosas
              </summary>
              <div className="mt-3 text-gray-700 text-sm space-y-2">
                <p>• Verifique o volume do dispositivo</p>
                <p>• Confirme as configurações de som nas notificações do sistema</p>
                <p>• No modo "Não Perturbar" as notificações podem ser silenciadas</p>
                <p>• Verifique as configurações específicas do navegador</p>
              </div>
            </details>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">
            Ainda com dúvidas? Entre em contato com o suporte técnico.
          </p>
          <button
            onClick={() => navigate('/tickets/new')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Abrir Ticket de Suporte
          </button>
        </div>
      </div>
    </div>
  );
}